import { AttachMoney, Email, Person, ShoppingCart } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import type { Order, OrderStatus, Package } from "../../types/api";
import { STATUS_OPTIONS } from "../../utils/dashboardHelpers";
import StatusChip from "../ui/StatusChip";

interface OrdersTabProps {
  orders: Order[];
  packages: Package[];
  campuses: Array<{ id: string; name: string; key: string }>;
  loading: boolean;
  error: string;
  campusFilter: string[];
  statusFilter: OrderStatus[];
  page: number;
  totalPages: number;
  onCampusFilterChange: (filter: string[]) => void;
  onStatusFilterChange: (filter: OrderStatus[]) => void;
  onPageChange: (page: number) => void;
  onOrderClick: (order: Order) => void;
}

const OrdersTab: React.FC<OrdersTabProps> = ({
  orders,
  packages,
  campuses,
  loading,
  error,
  campusFilter,
  statusFilter,
  page,
  totalPages,
  onCampusFilterChange,
  onStatusFilterChange,
  onPageChange,
  onOrderClick,
}) => {
  // Campus options (sorted alphabetically)
  const campusOptions = campuses.sort((a, b) => a.key.localeCompare(b.key));

  // Helper to get package details by ID
  const getPackageDetails = (packageId: string) => {
    return packages.find((pkg) => pkg.packageId === packageId);
  };

  // Filter orders by campus if filter is set (frontend filtering)
  const displayedOrders =
    campusFilter.length > 0
      ? orders.filter((order) => campusFilter.includes(order.campusId))
      : orders;

  return (
    <Box>
      {/* Filters - Campus and Status */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Campus Filter - Multi-Select Dropdown */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel>
              Campus
              {campusFilter.length > 0 ? ` (${campusFilter.length})` : ""}
            </InputLabel>
            <Select
              multiple
              value={campusFilter}
              onChange={(e) =>
                onCampusFilterChange(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value
                )
              }
              label={`Campus${
                campusFilter.length > 0 ? ` (${campusFilter.length})` : ""
              }`}
              renderValue={(selected) =>
                selected.length === 0
                  ? "All Campuses"
                  : selected.length === campusOptions.length
                  ? "All Campuses"
                  : `${selected.length} selected`
              }
            >
              {/* Individual Campus Options */}
              {campusOptions.map((campus) => (
                <MenuItem key={campus.id} value={campus.id}>
                  <Checkbox
                    checked={campusFilter.includes(campus.id)}
                    size="small"
                  />
                  <ListItemText primary={campus.key} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Status Filter - Multi-Select Dropdown */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel>
              Status
              {statusFilter.length > 0 ? ` (${statusFilter.length})` : ""}
            </InputLabel>
            <Select
              multiple
              value={statusFilter}
              onChange={(e) =>
                onStatusFilterChange(
                  typeof e.target.value === "string"
                    ? (e.target.value.split(",") as OrderStatus[])
                    : (e.target.value as OrderStatus[])
                )
              }
              label={`Status${
                statusFilter.length > 0 ? ` (${statusFilter.length})` : ""
              }`}
              renderValue={(selected) =>
                selected.length === 0
                  ? "All Statuses"
                  : `${selected.length} selected`
              }
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={statusFilter.includes(option.value)}
                    size="small"
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {/* Orders Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Campus</TableCell>
                  <TableCell>Package</TableCell>
                  <TableCell>Photos</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedOrders.map((order) => {
                  const packageDetails = getPackageDetails(order.packageId);
                  const orderDate = order.createdAt
                    ? new Date(order.createdAt).toLocaleDateString()
                    : "N/A";

                  return (
                    <TableRow
                      key={order.id}
                      hover
                      onClick={() => onOrderClick(order)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Avatar sx={{ bgcolor: "primary.main" }}>
                            <Person />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {order.parentInfo?.name || "Unknown Customer"}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Email
                                sx={{
                                  fontSize: 14,
                                  color: "text.secondary",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {order.parentInfo?.email || order.parentId}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.campusName || "Unknown Campus"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {packageDetails?.name || "Package N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {packageDetails
                            ? `${packageDetails.items.length} item(s)`
                            : "Details unavailable"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${order.photoIds.length} photos`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <StatusChip status={order.status} size="small" />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <AttachMoney
                            sx={{ fontSize: 16, color: "success.main" }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {packageDetails?.price.toFixed(2) || "0.00"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{orderDate}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {displayedOrders.length > 0 && totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => {
                  onPageChange(value);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Empty state */}
          {displayedOrders.length === 0 && !loading && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <ShoppingCart
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {campusFilter.length > 0 || statusFilter.length > 0
                  ? "No orders match your filters"
                  : "No orders yet"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {campusFilter.length > 0 || statusFilter.length > 0
                  ? "Try adjusting your filters"
                  : "Customer orders will appear here when photos are purchased"}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default OrdersTab;
